import { useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { Student } from '@/common/types/student';
import { handleLogout } from '@/common/utils/auth';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import ensureError, {
  formatErrorForNotification,
  NotFoundError,
} from '@/common/utils/error';
import { notifyZapier } from '@/common/utils/zapier';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { CaretDownIcon, SmileyXEyesIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { Button } from '@shadcn/components/button';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';

export default function ErrorPage(props: { error?: unknown }) {
  const routeError = useRouteError();
  const error = ensureError(props.error ?? routeError);

  const navigate = useNavigate();
  const location = useLocation();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);

  const hasNotified = useRef(false);
  const [showDetails, setShowDetails] = useState(false);

  console.error(error);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  useEffect(() => {
    if (hasNotified.current) return;
    hasNotified.current = true;

    let student: Student | undefined;
    try {
      student = getStudent();
    } catch {
      // will fail if GameInstance isn't instantiated - ignore it
    }

    notifyZapier({
      hook: 'error',
      userId: student?.id ?? userId ?? 'Unknown',
      organisationId:
        student?.organisationId ?? currentOrganisationId ?? 'Unknown',
      notifier: 'ErrorView',
      fatal: true,
      error: formatErrorForNotification(error),
    });
  }, [error, userId]);

  useEffect(() => {
    // Maintain a stack of visited paths to ensure we can always get back to the
    // default dashboard route in case of an error on every other page
    const navigationStack = JSON.parse(
      sessionStorage.getItem('navigationStack') || '[]',
    );
    if (!navigationStack.includes(location.pathname)) {
      navigationStack.push(location.pathname);
      sessionStorage.setItem(
        'navigationStack',
        JSON.stringify(navigationStack),
      );
    }
  }, [location.pathname]);

  // Get specific error message based on error type
  const getErrorMessage = () => {
    if (error instanceof NotFoundError) {
      return "We couldn't find the page you're looking for.";
    }
    return 'Sorry, an unexpected error has occurred.';
  };

  const handleGoBack = () => {
    const navigationStack = JSON.parse(
      sessionStorage.getItem('navigationStack') || '[]',
    );
    navigationStack.pop(); // Remove current path
    const previousPath = navigationStack[navigationStack.length - 1];

    if (previousPath) {
      sessionStorage.setItem(
        'navigationStack',
        JSON.stringify(navigationStack),
      );
      navigate(-1);
    } else {
      navigate(getDefaultDashboardRoute());
    }
  };

  return (
    <div className="h-svh w-full flex items-center justify-center">
      <div className="text-center space-y-4 p-8">
        <span className="flex justify-center">
          <SmileyXEyesIcon size={80} className="text-neutral-600" />
        </span>
        <h1 className="text-4xl font-bold text-neutral-900">Oops!</h1>
        <p className="text-lg text-neutral-600">{getErrorMessage()}</p>
        <div className="py-4">
          <Button onClick={handleGoBack} className="min-w-32">
            Go Back
          </Button>
        </div>
        {error.message && (
          <div className="space-y-2">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mx-auto"
            >
              <CaretDownIcon
                className={`transition-transform ${
                  showDetails ? 'rotate-180' : ''
                }`}
              />
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
            {showDetails && (
              <p className="text-sm text-neutral-500 italic">{error.message}</p>
            )}
          </div>
        )}
        <p className="text-sm text-neutral-500 pt-4">
          Still not working?{' '}
          <button
            onClick={handleLogout}
            className="text-primary hover:underline font-medium"
          >
            Try signing out
          </button>
        </p>
      </div>
    </div>
  );
}
